import { motion } from "framer-motion";
import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import * as TinyPopover from "react-tiny-popover";
import { ThemeContext } from "styled-components";
import { ICONSAX_SIZE } from "../Iconsax/Iconsax.types.js";
import { defaultTheme } from "../Theme/Theme.constants.js";
import { PopoverBodyContainer, PopoverCloseIcon } from "./Popover.style.js";
import { DefaultPopoverProps, PopoverProps, PopoverUtils } from "./Popover.types.js";
import { PopoverItem } from "./components/PopoverItem/PopoverItem.js";

const defaultProps: DefaultPopoverProps = {
    shape: "square",
    hideArrow: false,
    positions: ["bottom", "top", "right", "left"],
    positionLeft: "0px",
    positionTop: "0px",
    elevation: defaultTheme.elevationLevels.max,
};
const Popover = forwardRef<PopoverUtils, PopoverProps>((props, ref): JSX.Element | null => {
    const theme = useContext(ThemeContext);

    const {
        children,
        dropdownContent,
        onOpenChange,
        container,
        shape,
        open,
        closeButton,
        hideArrow,
        disableClickOutside,
        positions,
        positionLeft,
        positionTop,
        style,
        containerStyle,
        className,
        elevation,
        align,
        disableClickOnChildren,
        mountNode,
        arrowColor,
        arrowStyle,
        fitChildrenWidth,
        removePadding,
        popoverRef,
        popoverChildRef,
    } = {
        ...defaultProps,
        ...props,
    };

    const containerClassName = `popover-container${className ? ` ${className}` : ""}`;

    const [isOpen, setIsOpen] = useState<boolean>(open !== undefined ? open : false);

    useEffect(() => {
        if (open !== undefined && open !== isOpen) {
            setIsOpen(open);
            if (onOpenChange) onOpenChange(open);
        }
    }, [open]);

    useEffect(() => {
        if (onOpenChange !== undefined && isOpen !== open) {
            onOpenChange(isOpen);
        }
    }, [isOpen]);

    useImperativeHandle(
        ref,
        (): PopoverUtils => ({
            open: () => setIsOpen(true),
            close: () => setIsOpen(false),
            toggle: (value: boolean) => setIsOpen(value),
        }),
    );

    const getPopoverBody = ({ width }: { width?: number }) => (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
            <PopoverBodyContainer
                shape={shape}
                style={{ ...(style ?? {}), width: fitChildrenWidth ? width : undefined }}
                removePadding={removePadding}
            >
                {closeButton && <PopoverCloseIcon size={ICONSAX_SIZE.SMALL} onClick={() => setIsOpen(false)} />}
                {dropdownContent}
            </PopoverBodyContainer>
        </motion.div>
    );

    return (
        <TinyPopover.Popover
            align={align}
            isOpen={isOpen}
            positions={positions}
            containerStyle={{
                ...(containerStyle as Record<string, string>),
                ...(positionLeft === "0px" && positionTop === "0px"
                    ? {
                          zIndex: `${elevation}`,
                      }
                    : {
                          zIndex: `${elevation}`,
                          top: positionTop,
                          left: positionLeft,
                      }),
            }}
            ref={popoverRef}
            content={({ position, childRect, popoverRect }) => (
                <div ref={popoverChildRef}>
                    {!hideArrow ? (
                        <TinyPopover.ArrowContainer
                            childRect={childRect}
                            popoverRect={popoverRect}
                            position={position}
                            arrowSize={7}
                            arrowColor={arrowColor ?? theme.grayscale.white}
                            arrowStyle={arrowStyle}
                        >
                            {getPopoverBody({ width: childRect.width })}
                        </TinyPopover.ArrowContainer>
                    ) : (
                        getPopoverBody({ width: childRect.width })
                    )}
                </div>
            )}
            containerClassName={containerClassName}
            onClickOutside={(event) => {
                if (disableClickOutside) return;
                if (event.defaultPrevented) return;
                setIsOpen(false);
            }}
            parentElement={mountNode || container}
        >
            {children ? (
                React.cloneElement(children, {
                    onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                        if (!disableClickOnChildren) {
                            setIsOpen(!isOpen);
                        }
                        if (children?.props.onClick !== undefined) children.props.onClick(e);
                    },
                })
            ) : (
                <> </>
            )}
        </TinyPopover.Popover>
    );
});

const PopoverComponents = {
    Item: PopoverItem,
};

export { Popover, PopoverComponents };
